import { useCallback, useEffect, useRef } from 'react';

/**
 * A lightweight custom hook that provides debounce functionality
 * without requiring the entire lodash library.
 *
 * @param callback - The function to debounce
 * @param delay - The delay in milliseconds
 * @returns An array containing the debounced function and a cancel function
 *
 * @example
 * // Usage example
 * const [debouncedSearch] = useDebounce((query) => {
 *   fetchSearchResults(query);
 * }, 300);
 *
 * // Call like a normal function
 * debouncedSearch('search term');
 */
export function useDebounce<T extends (...args: unknown[]) => void>(
  callback: T,
  delay: number
): [(...args: Parameters<T>) => void, () => void] {
  // Use useRef to persist the timeout between renders without causing effects
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
 
  // Create a debounced version of the callback
  const debouncedFunction = useCallback((...args: Parameters<T>) => {
    // Clear existing timeout if present
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
   
    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);
 
  // Function to manually cancel the debounce
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
 
  // Clean up on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);
 
  return [debouncedFunction, cancel];
}

/**
 * A simple throttle function that ensures a function is not called more often than the specified interval
 *
 * @param callback - The function to throttle
 * @param interval - The minimum time between function calls in milliseconds
 * @returns A throttled version of the callback function
 *
 * @example
 * // Usage example
 * const throttledScroll = useThrottle(() => {
 *   // Handle scroll event
 * }, 100);
 *
 * window.addEventListener('scroll', throttledScroll);
 */
export function useThrottle<T extends (...args: unknown[]) => void>(
  callback: T,
  interval: number
): [(...args: Parameters<T>) => void, () => void] {
  const lastTime = useRef<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
 
  const throttledFunction = useCallback((...args: Parameters<T>) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastTime.current;
   
    // If enough time has passed, call the function immediately
    if (timeSinceLastCall >= interval) {
      lastTime.current = now;
      callback(...args);
    } else {
      // Otherwise, schedule it to be called when the interval has passed
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
     
      timeoutRef.current = setTimeout(() => {
        lastTime.current = Date.now();
        callback(...args);
        timeoutRef.current = null;
      }, interval - timeSinceLastCall);
    }
  }, [callback, interval]);
 
  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);
 
  // Clean up on unmount
  useEffect(() => {
    return cancel;
  }, [cancel]);
 
  return [throttledFunction, cancel];
}